import { Link } from "wouter";
import {
  allServiceLinks,
  neighboringAreasByPath,
  relatedServicesByPath,
  residentialServiceLinks,
  routeLabels,
  serviceAreaLinks,
} from "@/lib/site-navigation";

function breadcrumbItems(path: string) {
  const label = routeLabels[path];
  if (!label) return [];

  if (path.startsWith("/service-areas/")) {
    return [
      { name: "Home", path: "/" },
      { name: "Service Areas", path: "/#areas-we-serve" },
      { name: label, path },
    ];
  }

  if (path === "/commercial-services/skid-steer-services") {
    return [
      { name: "Home", path: "/" },
      { name: "Commercial Services", path: "/commercial-services" },
      { name: label, path },
    ];
  }

  if (residentialServiceLinks.some((service) => service.path === path)) {
    return [
      { name: "Home", path: "/" },
      { name: "Services", path: "/#services" },
      { name: label, path },
    ];
  }

  return [
    { name: "Home", path: "/" },
    { name: label, path },
  ];
}

export function Breadcrumbs({ path }: { path: string }) {
  const items = breadcrumbItems(path);
  if (items.length === 0) return null;

  return (
      <nav
        aria-label="Breadcrumb"
        className="border-b border-gray-200 bg-white"
      >
        <ol className="container mx-auto flex flex-wrap items-center gap-2 px-4 py-3 text-sm text-gray-600 md:px-6">
          {items.map((item, index) => (
            <li key={`${item.path}-${item.name}`} className="flex items-center gap-2">
              {index > 0 && <span aria-hidden="true">›</span>}
              {index === items.length - 1 ? (
                <span aria-current="page" className="font-medium text-[#1a3a1a]">
                  {item.name}
                </span>
              ) : (
                <Link href={item.path} className="hover:text-[#2a7a2a] hover:underline">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
  );
}

function LinkGrid({ links }: { links: { name: string; path: string }[] }) {
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {links.map((link) => (
        <li key={link.path}>
          <Link
            href={link.path}
            className="block rounded-lg border border-gray-200 bg-white px-4 py-3 font-medium text-[#1a3a1a] transition-colors hover:border-[#2a7a2a] hover:text-[#2a7a2a]"
          >
            {link.name}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ServicePageLinks({ path }: { path: string }) {
  const related = relatedServicesByPath[path];
  if (!related) return null;

  return (
    <section className="bg-[#f7f3ed] py-16" aria-labelledby="service-areas-heading">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <h2 id="service-areas-heading" className="mb-4 text-3xl text-[#1a3a1a]">
          Where we provide this service
        </h2>
        <p className="mb-8 max-w-3xl text-gray-700">
          TLC Associates LLC serves homeowners, businesses, and property managers
          throughout Norfolk County, Massachusetts.
        </p>
        <LinkGrid links={serviceAreaLinks} />

        <h2 className="mb-6 mt-12 text-2xl text-[#1a3a1a]">Related services</h2>
        <LinkGrid links={related} />
      </div>
    </section>
  );
}

function AreaPageLinks({ path }: { path: string }) {
  const neighbors = neighboringAreasByPath[path];
  if (!neighbors) return null;
  return (
    <section className="bg-white py-16" aria-labelledby="area-services-heading">
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <h2 id="area-services-heading" className="mb-6 text-3xl text-[#1a3a1a]">
          Landscaping services available in this area
        </h2>
        <LinkGrid links={allServiceLinks} />

        <p className="mt-10 text-gray-700">
          <span className="font-semibold text-[#1a3a1a]">Nearby areas: </span>
          {neighbors.map((neighbor, index) => (
            <span key={neighbor.path}>
              {index > 0 && ", "}
              <Link href={neighbor.path} className="text-[#2a7a2a] hover:underline">
                {neighbor.name}
              </Link>
            </span>
          ))}
          .
        </p>
      </div>
    </section>
  );
}

export function PageCrossLinks({ path }: { path: string }) {
  return (
    <>
      <ServicePageLinks path={path} />
      <AreaPageLinks path={path} />
    </>
  );
}